import { Component, HostListener, Input, OnInit } from '@angular/core';

import { Observable, Subscription, catchError, map, mergeMap, of, take, tap } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DisplayGroupModel } from '../../../Layout/models/display-group.model';
import { DisplayGroupsService } from '../../../Layout/services/display-groups.service';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';


@Component({
    selector: 'agpr-new-resource-dialog',
    templateUrl: './new-resource-dialog.component.html',
    styleUrls: ['./new-resource-dialog.component.scss'],
    standalone: false
})
export class NewResourceDialogComponent extends AbstractDialogComponent implements OnInit {

    @Input() id: string = 'newResourceDialog';
    @Input() width: number = 300;
    @Input() heading: string;

    @Input() description: string = 'Common.dialog.newResource.name.label';
    @Input() placeholder: string = 'Common.dialog.newResource.name.placeholder';
    @Input() maxLength: number = 64;
    @Input() okLabel: string = 'Core.button.ok';
    @Input() cancelLabel: string = 'Core.button.cancel';
    @Input() disabledButtonTooltip: string = 'Common.dialog.newResource.name.requiredTooltip';

    @Input() addToGroup: boolean = false;
    @Input() currentGroup: number;
    @Input() initialValue: string;

    @Input() formValidator: (text: string) => string[];
    @Input() observableFormValidator: (text: string) => Observable<string[]>;
    @Input() submit: (text: string, group?: DisplayGroupModel) => Observable<any>;

    isValid: boolean = false;
    showLoading: boolean = false;

    name: string = '';
    selectedGroup: DisplayGroupModel;
    groupOptions: Observable<DisplayGroupModel[]>;
    errors: string[] = [];

    private subscriptions: Subscription[] = [];

    readonly buttonStyle = ButtonTypesEnum;
    readonly autocompleteCustomOptions: AutocompleteCustomOption[] = [
        {
            label: 'Common.lhp.groupSelectionDialog.autocomplete.createGroup',
            clearOnSelect: false,
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            action: (name: string) => this.createNewGroup(name)
        }
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        private displayGroupsService: DisplayGroupsService
    ) {
        super(coreDialogService, 'saveNewResource');
    }

    @HostListener('window:keyup.escape', ['$event']) onKeyDown(e: KeyboardEvent) {
        this.cancel();
    }

    ngOnInit() {
        this.groupOptions = this.displayGroupsService.getGroups();

        if (this.currentGroup) {
            this.subscriptions.push(
                this.displayGroupsService.getGroups().subscribe(groups => {
                    if (!this.selectedGroup) {
                        this.selectedGroup = groups.find(group => group.id === this.currentGroup);
                    }
                })
            );
        }

        if (this.initialValue) {
            this.name = this.initialValue;
            this.validate$().subscribe();
        }
    }


    ok() {
        this.name = this.name ? this.name.trim() : '';
        if (!this.addToGroup) {
            this.selectedGroup = null;
        }
        this.validate$().pipe(
            take(1),
            mergeMap(isValid => {
                if (isValid) {
                    this.showLoading = true;
                    return this.submit(this.name, this.selectedGroup).pipe(tap(() => this.closeDialog()));
                }

                return of(null);
            }),
            catchError(() => {
                this.showLoading = false;
                return of(null);
            })
        ).subscribe();
    }

    cancel() {
        this.closeDialog();
    }


    clearErrors() {
        this.isValid = !!this.name?.trim();
        this.errors = [];
    }

    onAddToGroupChange() {
        if (!this.addToGroup) {
            this.selectedGroup = null;
        }
    }


    private validate$(): Observable<boolean> {
        const trimmed = this.name?.trim();
        let res$ = of(true);

        if (trimmed) {
            if (this.formValidator) {
                this.errors = this.formValidator(trimmed);
                res$ = of(!this.errors?.length);
            } else if (this.observableFormValidator) {
                res$ = this.observableFormValidator(trimmed).pipe(
                    map(value => {
                        this.errors = value;
                        return !this.errors?.length;
                    })
                );
            }
        } else {
            this.errors = [];
            res$ = of(false);
        }



        return res$.pipe(
            tap(isValid => this.isValid = isValid),
            take(1)
        );
    }


    private createNewGroup(name: string) {
        const group = new DisplayGroupModel();
        group.name = name;
        this.displayGroupsService.createGroup(group).subscribe({
            next: (id) => {
                group.id = id;
                this.selectedGroup = group;
            }
        });
    }
}
